<template>
  <div :class="[{ desktop: $isDesktop }]">
    <lazy-server-error-messages
      v-if="uiError"
      :error="uiError"
      class="post-a-review-server-error"
    />
    <add-a-review
      v-if="campsite && !isAddedState && !isErrorsState && !uiError"
      :campsite="campsite"
      :token="token"
      :booking="booking"
      :errors="(context && context.errors) || {}"
      :states="state ? state.toStrings() : []"
      :initial-overall-rating="initialOverallRating"
      @submit="submit($event)"
    />
    <div v-if="isAddedState" class="post-review-messages">
      <p data-test="review-published">
        <translate>Your review has been published on Pitchup.com.</translate>
        <pu-campsite-link v-bind="getCampsiteLinkProps()">
          <translate>Click here to view it.</translate>
        </pu-campsite-link>
      </p>
      <translate :tt="{ campsiteName }" tag="p">
        Thanks for reviewing {campsiteName}: your feedback helps.
      </translate>
    </div>
  </div>
</template>

<script setup lang="ts">
import { interpret } from 'xstate-v4'
import { type CampsiteDetails } from '~/apps/campsite-details/types'
import {
  type AddReviewStateMachineInterpreter,
  returnAddReviewStateMachine,
} from '../machine'
import { useFormStore } from '~/stores/useFormStore'
import { reportAppError } from '~/utility/sentry/apiErrorReporter'

const { $gtm, $isDesktop } = useNuxtApp()
const { t, i18n } = useI18n()
const formStore = process.client ? useFormStore(usePinia()) : undefined

const props = defineProps<{
  token: string
  campsite: CampsiteDetails

  initialBooking?: any
  initialError?: string | null
  initialOverallRating?: number
  setBackButtonVisibility?: (visible: boolean) => null
}>()

const title = t('Review {campsiteName}, {town}', {
  campsiteName: props.campsite.name,
  town: props.campsite.hierarchyTextShort,
})

const description = t('Rate your stay at {campsiteName}', {
  campsiteName: props.campsite.name,
})

useHead({
  title: title + ' | Pitchup.com',
  meta: [
    {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: description,
    },
  ],
})

let machine: AddReviewStateMachineInterpreter = returnAddReviewStateMachine(
  i18n.locale,
)
// Use shallowRef for XState - state/context are replaced on each transition
const context = shallowRef<
  AddReviewStateMachineInterpreter['state']['context'] | null
>(null)
const state = shallowRef<AddReviewStateMachineInterpreter['state'] | null>(null)
const campsiteName = computed(() => {
  return props.campsite.name
})

const campsiteSlug = computed(() => {
  return props.campsite.slug
})

const isAddedState = computed(() => {
  return state.value?.matches('added')
})

watch(isAddedState, (isAdded) => {
  if (props.setBackButtonVisibility) {
    props.setBackButtonVisibility(!isAdded)
  }
  // Clear review form data from localStorage when review is successfully submitted
  if (isAdded && formStore) {
    formStore.clearField('review')
  }
})

const isErrorsState = computed(() => {
  return state.value?.matches('errors')
})

watch(isErrorsState, (isError) => {
  if (isError) {
    try {
      const errors = state.value?.context.errors
      if (errors) {
        Object.keys(errors).forEach((key) => {
          const gtmEvent = {
            event: 'review_form_error',
            field: key.includes('caption') ? 'caption' : key,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type errors object with proper structure
            message: errors[key],
            trigger: 'submit',
          }

          $gtm.push(gtmEvent)
        })
      }
    } catch (_e: unknown) {
      reportAppError(_e, 'review form GTM error tracking', {
        campsiteSlug: props.campsite.slug,
        campsiteName: props.campsite.name,
        hasErrors: !!state.value?.context.errors,
        errorKeys: state.value?.context.errors
          ? Object.keys(state.value.context.errors)
          : [],
      })
    }
  }
})

const booking = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type booking context properly
  return context.value?.booking || props.initialBooking || null
})

// Prefer machine error (submission) and fallback to SSR initial error (token validation)

/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- XState context.error is untyped */
const uiError = computed(
  () =>
    (context.value && (context.value as any).error) || props.initialError || '',
)
/* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access */

onMounted(() => {
  // If there is an SSR error (invalid token, mismatch, etc.), do not start the machine
  if (uiError.value) return

  if (!machine) {
    throw new Error('no review machine')
  }

  machine = interpret(machine)
  machine
    .onTransition((newState, _event) => {
      if (newState.changed) {
        state.value = newState
        context.value = newState.context
      }
    })
    .start()
})

function submit(reviewData): void {
  if (!booking.value) {
    throw new Error('no booking in reviews')
  }
  machine?.send({
    type: 'SUBMIT_REVIEW',
    token: props.token,
    campsiteSlug: campsiteSlug.value,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type reviewData and booking properly
    reviewData: { ...reviewData, visitDate: booking.value.arrive },
  })
}

function getCampsiteLinkProps() {
  if (context.value && context.value.reviewId) {
    return {
      hierarchyPath: props.campsite.path,
      slug: props.campsite.slug,
      reviewId: context.value.reviewId,
      excludeType: 'postCampsiteReview',
      forceReviewId: true,
    }
  }
  return {
    hierarchyPath: props.campsite.path,
    slug: props.campsite.slug,
    excludeType: 'postCampsiteReview',
  }
}

// No auth checks here: booking is SSR-validated via token.
</script>

<style scoped>
.post-a-review-server-error,
.post-review-messages {
  padding: 1.4em;
  font-size: var(--body);
  line-height: 1.5;
  max-width: 60ch;
  margin: auto;
  min-height: 90dvh;
}

.desktop .post-a-review-server-error,
.desktop .post-review-messages {
  font-size: inherit;
}
</style>
